import { useCallback, useRef, type FocusEventHandler, type HTMLAttributes, type MouseEventHandler } from "react";

export const SUPPRESSED_AUTOCOMPLETE_VALUE = "new-user-value";
export const SUPPRESSED_PASSWORD_AUTOCOMPLETE_VALUE = "new-password";

export function resolveAutocompleteValue(autoComplete: string | undefined, type?: string): string {
  if (autoComplete !== undefined) {
    return autoComplete;
  }

  return type === "password" ? SUPPRESSED_PASSWORD_AUTOCOMPLETE_VALUE : SUPPRESSED_AUTOCOMPLETE_VALUE;
}

export function shouldSuppressBrowserAutocomplete(
  autoComplete: string | undefined,
  { readOnly, type }: { readOnly?: boolean; type?: string } = {}
): boolean {
  if (autoComplete !== undefined) {
    return false;
  }

  if (readOnly) {
    return false;
  }

  if (type === "password") {
    return false;
  }

  return true;
}

export function getBrowserAutocompleteSuppressionAttributes(suppress: boolean): Pick<
  HTMLAttributes<HTMLElement>,
  "autoCorrect" | "spellCheck"
> & {
  "data-1p-ignore"?: true;
  "data-lpignore"?: true;
} {
  if (!suppress) {
    return {};
  }

  return {
    autoCorrect: "off",
    spellCheck: false,
    "data-1p-ignore": true,
    "data-lpignore": true
  };
}

interface UseSuppressBrowserAutocompleteOptions {
  readOnly?: boolean;
  type?: string;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onMouseDown?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export function useSuppressBrowserAutocomplete(
  autoComplete: string | undefined,
  { readOnly, type, onFocus, onMouseDown }: UseSuppressBrowserAutocompleteOptions = {}
) {
  const isUnlockedRef = useRef(false);
  const suppress = shouldSuppressBrowserAutocomplete(autoComplete, { readOnly, type });

  const unlockField = useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement) => {
      if (suppress && !isUnlockedRef.current) {
        isUnlockedRef.current = true;
        element.readOnly = false;
      }
    },
    [suppress]
  );

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
    event => {
      unlockField(event.currentTarget);
      onFocus?.(event);
    },
    [unlockField, onFocus]
  );

  const handleMouseDown = useCallback<MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
    event => {
      unlockField(event.currentTarget);
      onMouseDown?.(event);
    },
    [unlockField, onMouseDown]
  );

  const resolvedReadOnly = readOnly || (suppress && !isUnlockedRef.current) || undefined;

  return {
    autoComplete: resolveAutocompleteValue(autoComplete, type),
    readOnly: resolvedReadOnly,
    onFocus: suppress || onFocus ? handleFocus : onFocus,
    onMouseDown: suppress || onMouseDown ? handleMouseDown : onMouseDown,
    suppressBrowserAutocomplete: suppress,
    ...getBrowserAutocompleteSuppressionAttributes(suppress)
  };
}
